import { useMemo } from "react";
import { getCalendarDays } from "@/lib/dateUtils";
import { groupEventsByDate } from "@/lib/eventsByDate";
import type { Event } from "./EventCard";
import { CalendarDayCell } from "./CalendarDayCell";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface CalendarGridProps {
  currentMonth: Date;
  events: Event[];
  onDayClick: (dateString: string, dayEvents: Event[]) => void;
}

export function CalendarGrid({ currentMonth, events, onDayClick }: CalendarGridProps) {
  const calendarDays = useMemo(() => getCalendarDays(currentMonth), [currentMonth]);

  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  return (
    <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-border/50">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-2.5 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid — the gap is the gridline, so the cells own the colour
          and the lines never double up at the seams. */}
      <div className="grid grid-cols-7 gap-px bg-border/50">
        {calendarDays.map((day) => (
          <CalendarDayCell
            key={day.dateString}
            day={day}
            events={eventsByDate.get(day.dateString) ?? []}
            onClick={() => onDayClick(day.dateString, eventsByDate.get(day.dateString) ?? [])}
          />
        ))}
      </div>
    </div>
  );
}
